import { Fragment } from 'react';
import { Button, Cell, DeprecatedIcon, Spacer, Text } from '@vinted/web-ui';
import { compact } from 'lodash';
import SeparatedList from 'components/SeparatedList';
import useTranslate from 'hooks/useTranslate';
import { USER_STATUS_MEMBER_DELETED } from 'constants/index';
import { MEMBER_RESTORE_URL } from 'constants/routes';
import FundraiserInfo from '../ProfileInfo/FundraiserInfo';
import UserInfoBlock from './UserInfoBlock';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const UserStatuses = _ref => {
  let {
    user,
    screen,
    isAvailableForMigration,
    showSupportFunctionality,
    isShadowBanned,
    viewingSelf,
    onUnblockClick
  } = _ref;
  const translate = useTranslate('user');
  const {
    fundraiser,
    moderator,
    volunteer_moderator,
    is_identity,
    is_god,
    is_hated,
    account_ban_date,
    forum_ban_date,
    account_status,
    id,
    is_account_ban_permanent,
    is_forum_ban_permanent
  } = user;

  function getRestrictionTranslationKey(key, isRestrictionPermanent) {
    const isDateVisibleForUser = viewingSelf || showSupportFunctionality || user.is_god;
    return isDateVisibleForUser && !isRestrictionPermanent ? key : "".concat(key, "_no_date");
  }

  function renderDonationStatus() {
    if (!(fundraiser !== null && fundraiser !== void 0 && fundraiser.active)) return null;
    return /*#__PURE__*/_jsx(FundraiserInfo, {
      user: user,
      screen: screen,
      styling: Cell.Styling.Tight
    });
  }

  function renderMigrationStatus() {
    if (!isAvailableForMigration || !showSupportFunctionality && !is_god) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "check-circle-filled",
      iconColor: DeprecatedIcon.Color.Success,
      children: translate('status.available_for_migration')
    });
  }

  function renderProductTeamStatus() {
    if (!moderator) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "shield-outlined",
      iconColor: DeprecatedIcon.Color.Expose,
      children: translate('status.product_team')
    });
  }

  function renderVolunteerStatus() {
    if (!volunteer_moderator) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "shield-outlined",
      iconColor: DeprecatedIcon.Color.Success,
      children: translate('status.volunteer_moderator')
    });
  }

  function renderIdentityStatus() {
    if (!is_identity) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "check-circle",
      iconColor: DeprecatedIcon.Color.Success,
      children: translate('status.identity_confirmed')
    });
  }

  function renderBannedStatus() {
    if (!account_ban_date || is_hated) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "block",
      iconColor: DeprecatedIcon.Color.Warning,
      children: /*#__PURE__*/_jsx(Text, {
        theme: "warning",
        children: translate(getRestrictionTranslationKey('admin.messages.banned', !!is_account_ban_permanent), {
          date: account_ban_date
        })
      })
    });
  }

  function renderBlockedStatus() {
    if (!is_hated) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "block",
        iconColor: DeprecatedIcon.Color.Warning,
        children: /*#__PURE__*/_jsx(Text, {
          theme: "warning",
          children: translate('admin.messages.blocked')
        })
      }), onUnblockClick && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Spacer, {
          size: Spacer.Size.Large
        }), /*#__PURE__*/_jsx(Button, {
          testId: "unblock-user-button",
          inline: true,
          text: translate('action.unblock'),
          styling: Button.Styling.Filled,
          onClick: onUnblockClick
        })]
      })]
    });
  }

  function renderForumBannedStatus() {
    if (!forum_ban_date || is_hated) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "block",
      iconColor: DeprecatedIcon.Color.Warning,
      children: /*#__PURE__*/_jsx(Text, {
        theme: "warning",
        children: translate(getRestrictionTranslationKey('admin.messages.forum_banned', !!is_forum_ban_permanent), {
          date: forum_ban_date
        })
      })
    });
  }

  function renderShadowBannedStatus() {
    if (!isShadowBanned) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "block",
      iconColor: DeprecatedIcon.Color.Warning,
      children: /*#__PURE__*/_jsx(Text, {
        theme: "warning",
        children: translate('admin.messages.shadow_banned_no_date')
      })
    });
  }

  function renderDeletedStatus() {
    if (account_status !== USER_STATUS_MEMBER_DELETED) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "block",
      iconColor: DeprecatedIcon.Color.Warning,
      children: /*#__PURE__*/_jsx(Text, {
        theme: "warning",
        children: translate('admin.messages.deleted', {
          'member-restore-url': chunks => /*#__PURE__*/_jsx("a", {
            href: MEMBER_RESTORE_URL(id),
            children: chunks
          })
        })
      })
    });
  }

  const statuses = compact([renderDonationStatus(), renderMigrationStatus(), renderProductTeamStatus(), renderVolunteerStatus(), renderIdentityStatus(), renderBannedStatus(), renderBlockedStatus(), renderForumBannedStatus(), renderShadowBannedStatus(), renderDeletedStatus()]);
  if (!statuses.length) return null;
  return /*#__PURE__*/_jsx(Cell, {
    children: /*#__PURE__*/_jsx(SeparatedList, {
      separator: /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Small
      }),
      children: statuses.map((status, index) => /*#__PURE__*/_jsx(Fragment, {
        children: status
      }, index))
    })
  });
};

export default UserStatuses;